import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractBusinessProfileService } from '../../abstracts/services/onboarding/business-profile.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IOptions } from '../../interfaces/common/common/options.interface';
import { IAnnualRevenue } from '../../interfaces/onboarding/business-profile/annual-revenue.interface';
import { IBusinessInvoice } from '../../interfaces/onboarding/business-profile/business-Invoice.interface';
import { IBusinessIndustry } from '../../interfaces/onboarding/business-profile/business-industry.interface';
import { IMultiOptions } from '../../interfaces/onboarding/business-profile/business-multi-options.interface';
import { IBusinessProfile } from '../../interfaces/onboarding/business-profile/business-profile.interface';
import { IBusinessType } from '../../interfaces/onboarding/business-profile/business-type.interface';
import { ICompanyInfo } from '../../interfaces/onboarding/business-profile/company-Info-model.interface';
import { ICustomerType } from '../../interfaces/onboarding/business-profile/customer-type.interface';
import { IEmployeeCount } from '../../interfaces/onboarding/business-profile/employee-count.interface';
import { IScoreRequest } from '../../interfaces/onboarding/business-profile/score-request.interface';
import { IYearFounded } from '../../interfaces/onboarding/business-profile/year-founded.interface';
import { ITellUsResponse } from '../../interfaces/onboarding/create-account/tell-us-reponse.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import { selectAllBusinessProfile } from '../../store/onboarding/reducers+selectors/business-profile.selector';
import { ServersideAlertService } from './serverside-alert.service';

import { CompanyResultViewTypes } from '../../enums/company-result-view.enum';
import { IHeardAboutUs } from '../../interfaces/onboarding/business-profile/heard-about-us.interface';
import { ISupportMethod } from '../../interfaces/onboarding/business-profile/support-method.interface';
import { destructSubscriptionArray } from '../../ui-helpers/functions/destruct-subscription-array';
import {
  applyEnrollmentIdToQueryParams,
  applyUserEmailToQueryParams,
  applyUserProfileId,
} from '../../ui-helpers/functions/user-context-url.func';

@Injectable({
  providedIn: 'root',
})
export class BusinessProfileService
  extends AbstractBusinessProfileService
  implements OnDestroy
{
  private apiUrl = environment.apiUrl;

  private businessType: IBusinessType[];
  private businessIndustry: IBusinessIndustry[];
  private annualRevenue: IAnnualRevenue[];
  private businessInvoice: IBusinessInvoice[];
  private yearFounded: IYearFounded[];
  private heardAboutUs: IHeardAboutUs[];
  private customerType: ICustomerType[];
  private supportMethods: ISupportMethod[];
  private employeeCount: IEmployeeCount[];
  private companyInfo: ICompanyInfo;
  private enumValue: CompanyResultViewTypes;
  private options: IOptions[];
  private multiOptions: IMultiOptions[];

  properties$: Observable<any>;
  payload: IBusinessProfile;
  subscriptions: Subscription[] = [];

  private businessControllerSuffix = 'Onboarding/Onboarding/businessprofile/';
  private sharedControllerSuffix = 'Onboarding/Onboarding/shared/';
  registerResponse: ITellUsResponse;
  constructor(
    private httpClient: HttpClient,
    public ssas: ServersideAlertService,
    private store: Store<IAppState>,
  ) {
    super();
  }

  ngOnDestroy() {
    destructSubscriptionArray(this.subscriptions);
  }

  async loadLegalFormOfBusiness(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}legal-form-of-business`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.businessType = [];
    else
      this.businessType = (result.data as []).map(
        (data: IBusinessType) => data,
      );

    return this.mapBusinessTypeToOptions(this.businessType);
  }

  async loadBusinessIndustry(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}industry`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.businessIndustry = [];
    else
      this.businessIndustry = (result.data as []).map(
        (data: IBusinessIndustry) => data,
      );

    return this.mapBusinessIndustryToOptions(this.businessIndustry);
  }

  async loadAnnualRevenue(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}annual-revenue`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.annualRevenue = [];
    else
      this.annualRevenue = (result.data as []).map(
        (data: IAnnualRevenue) => data,
      );

    return this.mapAnnualRevenueToOptions(this.annualRevenue);
  }

  async loadBusinessInvoice(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}business-invoice`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.businessInvoice = [];
    else
      this.businessInvoice = (result.data as []).map(
        (data: IBusinessInvoice) => data,
      );

    return this.mapBusinessInvoiceToOptions(this.businessInvoice);
  }

  async loadYearFounded(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}years`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.yearFounded = [];
    else
      this.yearFounded = (result.data as []).map((data: IYearFounded) => data);

    return this.mapYearFoundedToOptions(this.yearFounded);
  }

  async loadHeardAboutUs(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}heard-about-us`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.heardAboutUs = [];
    else
      this.heardAboutUs = (result.data as []).map(
        (data: IHeardAboutUs) => data,
      );

    return this.mapHeardAboutUsToOptions(this.heardAboutUs);
  }

  async loadEmployeeCount(): Promise<IOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}employee-count`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.employeeCount = [];
    else
      this.employeeCount = (result.data as []).map(
        (data: IEmployeeCount) => data,
      );

    return this.mapEmployeeCountToOptions(this.employeeCount);
  }

  async loadCustomerType(): Promise<IMultiOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}customer-type`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.customerType = [];
    else
      this.customerType = (result.data as []).map(
        (data: ICustomerType) => data,
      );

    return this.mapCustomerTypeToOptions(this.customerType);
  }

  async loadSupportMethods(): Promise<IMultiOptions[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}support`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(),
    );
    if (result === null || result.data === null) this.supportMethods = [];
    else
      this.supportMethods = (result.data as []).map(
        (data: ISupportMethod) => data,
      );

    return this.mapSupportMethodToOptions(this.supportMethods);
  }

  async submitBusinessProfileData(
    businessProfile: IBusinessProfile,
  ): Promise<IApiResponse> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(
        `${this.apiUrl}${this.businessControllerSuffix}save-business-data`,
        businessProfile,
        { params },
      ),
    );
    return result;
  }

  async saveCustomerTypes(modelList: IMultiOptions[]): Promise<IApiResponse> {
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(
        `${this.apiUrl}${this.businessControllerSuffix}save-customer-types`,
        modelList,
      ),
    );
    return result;
  }

  async saveSupportMethods(modelList: IMultiOptions[]): Promise<IApiResponse> {
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(
        `${this.apiUrl}${this.businessControllerSuffix}save-support-methods`,
        modelList,
      ),
    );
    return result;
  }

  async confirmCompanyInfo(
    scoreRequest: IScoreRequest,
  ): Promise<CompanyResultViewTypes> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    params = applyUserProfileId(params, this.store);
    params = applyUserEmailToQueryParams(params, this.store);
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(
        `${this.apiUrl}${this.businessControllerSuffix}confirm-company`,
        scoreRequest,
        { params },
      ),
    );
    if (result === null || result.data === null)
      this.enumValue = CompanyResultViewTypes.NotQualified;
    else this.enumValue = result.data as unknown as CompanyResultViewTypes;

    return this.enumValue;
  }

  async getBestMatch(): Promise<ICompanyInfo> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.businessControllerSuffix}confirm`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }).pipe(),
    );
    if (result === null || result.data === null) this.companyInfo;
    else this.companyInfo = result.data as ICompanyInfo;

    return this.companyInfo;
  }

  async checkRescoreEligability(): Promise<boolean> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.businessControllerSuffix}check-rescore-eligability`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }),
    );
    return result.data as boolean;
  }

  async rescoreProspect(): Promise<CompanyResultViewTypes> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    params = applyUserEmailToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.businessControllerSuffix}rescore-prospect`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }),
    );
    let result = CompanyResultViewTypes.NotQualified;
    if (response !== null && response.data !== null)
      result = response.data as unknown as CompanyResultViewTypes;
    return result;
  }

  async reactivateLead(): Promise<IApiResponse> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.businessControllerSuffix}reactivate-lead`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }),
    );
    return result;
  }

  mapBusinessIndustryToOptions(
    businessIndustryData: IBusinessIndustry[],
  ): IOptions[] {
    this.options = businessIndustryData.map((item) => ({
      id: item.id,
      value: item.name,
      selected: false,
    }));

    return this.options;
  }

  mapBusinessTypeToOptions(businessTypeData: IBusinessType[]): IOptions[] {
    this.options = businessTypeData.map((item) => ({
      id: item.id,
      value: item.name,
      selected: false,
    }));

    return this.options;
  }

  mapAnnualRevenueToOptions(businessTypeData: IAnnualRevenue[]): IOptions[] {
    this.options = businessTypeData.map((item) => ({
      id: item.id,
      value: item.annualRevenueRange,
      selected: false,
    }));

    return this.options;
  }

  mapBusinessInvoiceToOptions(
    businessInvoiceData: IBusinessInvoice[],
  ): IOptions[] {
    this.options = businessInvoiceData.map((item) => ({
      id: item.id,
      value: item.name,
      selected: false,
    }));

    return this.options;
  }

  mapYearFoundedToOptions(yearFoundedData: IYearFounded[]): IOptions[] {
    this.options = yearFoundedData.map((item) => ({
      id: item.id,
      value: item.yearFounded,
      selected: false,
    }));

    return this.options;
  }
  mapHeardAboutUsToOptions(heardAboutUsData: IHeardAboutUs[]): IOptions[] {
    this.options = heardAboutUsData.map((item) => ({
      id: item.id,
      value: item.name,
      selected: false,
    }));

    return this.options;
  }

  mapEmployeeCountToOptions(employeeCountData: IEmployeeCount[]): IOptions[] {
    this.options = employeeCountData.map((item) => ({
      id: item.id,
      value: item.name,
      selected: false,
    }));

    return this.options;
  }

  mapCustomerTypeToOptions(customerTypeData: ICustomerType[]): IMultiOptions[] {
    this.multiOptions = customerTypeData.map((item) => ({
      id: item.id,
      name: item.name,
      value: item.name,
      selected: false,
    }));

    return this.multiOptions;
  }

  mapSupportMethodToOptions(
    supportMethodData: ISupportMethod[],
  ): IMultiOptions[] {
    this.multiOptions = supportMethodData.map((item) => ({
      id: item.id,
      name: item.name,
      value: item.name,
      selected: false,
    }));

    return this.multiOptions;
  }

  private BuildPayload() {
    this.properties$ = this.store.select(selectAllBusinessProfile);
    this.subscriptions.push(
      this.properties$.subscribe((props) => {
        this.payload = {
          PageStateId: props?.pageStateId || '',
          LegalFormOfBusinessId: props?.businessType?.id || '',
          LegalFormOfBusinessName: props?.businessType?.value || '',
          BusinessDescription: props?.businessDescription || '',
          StateOfOrganization: props?.stateOfOrg?.value || '',
          AnnualRevenueId: props?.annualRevenue?.id || -1,
          AnnualRevenue: props?.annualRevenue?.value || '',
          YearFounded: props?.yearFounded || '',
          Website: props?.companyWebsite || '',
          NumberOfEmployees: props?.employeeCount?.value || '',
          NumberOfEmployeesId: props?.employeeCount?.id || '',
          DBA: props?.dba || '',
          IndustryBusinessId: props?.businessIndustry?.id || '',
          IndustryBusinessValue: props?.businessIndustry?.value || '',
          Address1: props?.companyAddress?.address1 || '',
          Address2: props?.companyAddress?.address2 || '',
          City: props?.companyAddress?.city || '',
          State: props?.companyAddress?.state || '',
          ZipCode: props?.companyAddress?.zipcode || '',
          InvoicingMethod: props?.invoicingMethod?.value || '',
          BusinessInvoiceId: props?.invoicingMethod?.id || '',
          CustomerTypes: props?.customerTypes,
          SupportMethods: props?.supportMethods,
          IndustryBusinessOthers: props?.businessIndustry?.value,
          BusinessInvoiceOthers: props?.invoicingMethod?.value,
        };
      }),
    );
  }
  public getPayload() {
    this.BuildPayload();
    return this.payload;
  }
}
